import { Dispatch, useCallback, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { DELETE_ACTIONS } from "../../../config/constants";
import { useCookie } from "../../../hooks/useCookie";
import { useToast } from "../../../hooks/useToast";
import { general } from "../../../locales/general";
import apiService from "../../../service/apiService";

export function useDeletePlace() {
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();
  const { toastSuccess, toastError } = useToast();

  const mutation = useMutation((id: string) =>
    apiService.MakeDeleteRequest(`places/${id}`, token),
  );

  const remove = (id: string) =>
    mutation.mutate(id, {
      onError: () => toastError(general.fr.message.operationFailed),
      onSuccess() {
        toastSuccess(general.fr.message.placeDeleted);
        queryClient.invalidateQueries("getAllPlaces");
      },
    });

  return { remove, isDeleting: mutation.isLoading };
}

export function useUploadMenu(placeId: string, onDone: () => void) {
  const { token } = useCookie("vToken");
  const { toastSuccess, toastError } = useToast();

  const mutation = useMutation((menu: File) =>
    apiService.MakePutRequest(`places/${placeId}/menu`, { menu }, token, true),
  );

  const upload = (menu: File) =>
    mutation.mutate(menu, {
      onSuccess() {
        toastSuccess(general.fr.message.menuAdded);
        onDone();
      },
      onError: () => toastError(general.fr.message.operationFailed),
    });

  return { upload, isUploading: mutation.isLoading };
}

/** Local checked state + keeps the bulk-delete list in sync */
export function useRowSelection(id: string, dispatch: Dispatch<BulkDeleteActions>) {
  const [isChecked, setIsChecked] = useState(false);

  const toggle = useCallback(
    (checked: boolean) => {
      setIsChecked(checked);
      dispatch({
        type: checked ? DELETE_ACTIONS.ADD : DELETE_ACTIONS.REMOVE,
        id,
      });
    },
    [dispatch, id],
  );

  // A row that unmounts (page change) must not stay in the list
  useEffect(
    () => () => dispatch({ type: DELETE_ACTIONS.REMOVE, id }),
    [dispatch, id],
  );

  return { isChecked, toggle };
}